import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Trash2, Pin, Hash, Home, Loader2, Plus, X, AtSign
} from 'lucide-react';
import {
  listGlobalPins,
  upsertGlobalPin,
  deleteGlobalPin,
  listTagPins,
  upsertTagPin,
  deleteTagPin,
  listHomepage,
  deleteHomepage,
  addHomepage,
  reorderHomepage,
  AdminPinDto,
  AdminTagPinDto,
  AdminHomepageView
} from '../api/pins';
import PageHeader from '../components/shared/PageHeader';
import { EmptyState } from '../components/shared/EmptyState';
import { ErrorBanner } from '../components/shared/ErrorBanner';
import { useConfirm } from '../components/shared/ConfirmDialog';
import { useUrlState } from '../hooks/useUrlState';

type TabType = 'global' | 'tag' | 'homepage';

// Sortable row component for homepage models
const SortableHomepageRow: React.FC<{
  model: AdminHomepageView;
  onDelete: (username: string) => void;
  isDeleting: boolean;
}> = ({ model, onDelete, isDeleting }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: model.username });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    background: isDragging ? 'var(--accent-soft)' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'selected' : undefined}>
      <td
        {...attributes}
        {...listeners}
        style={{ width: 36, padding: '6px 4px' }}
        aria-label={`Drag to reorder ${model.username}`}
      >
        <span className="hf-grip" title="Drag to reorder">
          <GripVertical size={14} />
        </span>
      </td>
      <td className="num" style={{ width: 60, color: 'var(--fg-3)' }}>
        <span className="hf-mono">{model.position}</span>
      </td>
      <td><span className="hf-mono">@{model.username}</span></td>
      <td style={{ color: 'var(--fg-2)' }}>{model.modelName || '—'}</td>
      <td style={{ width: 64 }}>
        <button
          type="button"
          className="hf-btn ghost sm"
          onClick={() => onDelete(model.username)}
          disabled={isDeleting}
          aria-label={`Remove ${model.username} from homepage`}
          title="Remove from homepage"
          style={{ color: 'var(--danger)' }}
        >
          <Trash2 size={13} />
        </button>
      </td>
    </tr>
  );
};

const ModelPinsPage: React.FC = () => {
  const confirm = useConfirm();
  const [urlState, setUrlState] = useUrlState({ tab: 'global' as TabType, tag: '' });
  const activeTab = urlState.tab;
  const setActiveTab = (t: TabType) => setUrlState({ tab: t });
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    kind: 'success' | 'danger';
    message: string;
  }>>([]);

  const [globalPinForm, setGlobalPinForm] = useState<AdminPinDto>({ username: '', position: 1 });
  const [tagPinForm, setTagPinForm] = useState<AdminTagPinDto>({ username: '', tagSlug: '', position: 1 });
  const selectedTagForList = urlState.tag;
  const setSelectedTagForList = (t: string) => setUrlState({ tag: t });
  const [homepageUsername, setHomepageUsername] = useState('');
  const [sortedHomepageModels, setSortedHomepageModels] = useState<AdminHomepageView[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const addNotification = (kind: 'success' | 'danger', message: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setNotifications(prev => [...prev, { id, kind, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  };

  // Queries
  const { data: globalPins = [], refetch: refetchGlobalPins } = useQuery({
    queryKey: ['globalPins'],
    queryFn: listGlobalPins,
    enabled: activeTab === 'global'
  });
  const { data: tagPins = [], refetch: refetchTagPins } = useQuery({
    queryKey: ['tagPins', selectedTagForList],
    queryFn: () => listTagPins(selectedTagForList),
    enabled: activeTab === 'tag' && !!selectedTagForList
  });
  const { data: homepageModels = [], refetch: refetchHomepage } = useQuery({
    queryKey: ['homepage'],
    queryFn: listHomepage,
    enabled: activeTab === 'homepage'
  });

  useEffect(() => {
    const sorted = [...homepageModels].sort((a, b) => a.position - b.position);
    setSortedHomepageModels(sorted);
  }, [homepageModels]);

  // Mutations
  const upsertGlobalMutation = useMutation({
    mutationFn: upsertGlobalPin,
    onSuccess: () => {
      addNotification('success', `Global pin for @${globalPinForm.username} saved`);
      setGlobalPinForm({ username: '', position: 1 });
      refetchGlobalPins();
    },
    onError: (error: any) => {
      addNotification('danger', error.response?.data?.message || 'Failed to save global pin');
    }
  });

  const deleteGlobalMutation = useMutation({
    mutationFn: deleteGlobalPin,
    onSuccess: () => { addNotification('success', 'Global pin removed'); refetchGlobalPins(); },
    onError: (error: any) => addNotification('danger', error.response?.data?.message || 'Delete failed')
  });

  const upsertTagMutation = useMutation({
    mutationFn: upsertTagPin,
    onSuccess: () => {
      addNotification('success', `Tag pin saved for @${tagPinForm.username}`);
      setTagPinForm({ username: '', tagSlug: '', position: 1 });
      refetchTagPins();
    },
    onError: (error: any) => addNotification('danger', error.response?.data?.message || 'Failed to save tag pin')
  });

  const deleteTagMutation = useMutation({
    mutationFn: ({ tagSlug, username }: { tagSlug: string; username: string }) =>
      deleteTagPin(tagSlug, username),
    onSuccess: () => { addNotification('success', 'Tag pin removed'); refetchTagPins(); },
    onError: (error: any) => addNotification('danger', error.response?.data?.message || 'Delete failed')
  });

  const addHomepageMutation = useMutation({
    mutationFn: addHomepage,
    onSuccess: () => {
      addNotification('success', `@${homepageUsername} added to homepage`);
      setHomepageUsername('');
      refetchHomepage();
    },
    onError: (error: any) => addNotification('danger', error.response?.data?.message || 'Failed to add')
  });

  const reorderHomepageMutation = useMutation({
    mutationFn: reorderHomepage,
    onSuccess: (result) => {
      addNotification('success', `Reordered ${result.updated} models`);
      refetchHomepage();
    },
    onError: (error: any) => {
      addNotification('danger', error.response?.data?.message || 'Failed to reorder');
      refetchHomepage();
    }
  });

  const deleteHomepageMutation = useMutation({
    mutationFn: deleteHomepage,
    onSuccess: () => { addNotification('success', 'Removed from homepage'); refetchHomepage(); },
    onError: (error: any) => addNotification('danger', error.response?.data?.message || 'Delete failed')
  });

  // Confirm helpers
  const confirmDelete = async (what: string, name: string) => {
    return confirm({
      title: `Remove ${what}?`,
      description: <>This will remove <span className="hf-mono">@{name}</span> from {what}. You can re-add it any time.</>,
      confirmLabel: 'Remove',
      tone: 'danger'
    });
  };

  const handleGlobalDelete = async (username: string) => {
    if (await confirmDelete('global pins', username)) {
      deleteGlobalMutation.mutate(username);
    }
  };
  const handleTagDelete = async (username: string) => {
    if (await confirmDelete(`tag pin (${selectedTagForList})`, username)) {
      deleteTagMutation.mutate({ tagSlug: selectedTagForList, username });
    }
  };
  const handleHomepageDelete = async (username: string) => {
    if (await confirmDelete('homepage', username)) {
      deleteHomepageMutation.mutate(username);
    }
  };

  // Form handlers
  const handleGlobalPinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!globalPinForm.username.trim()) {
      addNotification('danger', 'Please enter a username');
      return;
    }
    upsertGlobalMutation.mutate({ ...globalPinForm, username: globalPinForm.username.trim() });
  };
  const handleTagPinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tagPinForm.username.trim() || !tagPinForm.tagSlug.trim()) {
      addNotification('danger', 'Please enter both username and tag slug');
      return;
    }
    upsertTagMutation.mutate({
      ...tagPinForm,
      username: tagPinForm.username.trim(),
      tagSlug: tagPinForm.tagSlug.trim()
    });
  };
  const handleHomepageAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!homepageUsername.trim()) {
      addNotification('danger', 'Please enter a username');
      return;
    }
    addHomepageMutation.mutate({ username: homepageUsername.trim() });
  };

  // DnD
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedHomepageModels.findIndex(m => m.username === active.id);
    const newIndex = sortedHomepageModels.findIndex(m => m.username === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedHomepageModels, oldIndex, newIndex);
    setSortedHomepageModels(reordered);
    reorderHomepageMutation.mutate({ usernames: reordered.map(m => m.username) });
  };

  return (
    <>
      <PageHeader
        title="Model Pins"
        subtitle="Manage pinned models for global pages, tag pages, and the homepage."
      />
      <div className="hf-content">
        {/* Toast region */}
        <div
          style={{
            position: 'fixed', top: 16, right: 16,
            display: 'flex', flexDirection: 'column', gap: 8,
            zIndex: 'var(--z-toast, 1200)', maxWidth: 380, pointerEvents: 'none'
          }}
          role="region"
          aria-label="Notifications"
        >
          {notifications.map((notif) => (
            <div key={notif.id} style={{ pointerEvents: 'auto' }}>
              <ErrorBanner
                kind={notif.kind === 'danger' ? 'danger' : 'success'}
                message={notif.message}
                onDismiss={() =>
                  setNotifications(prev => prev.filter(n => n.id !== notif.id))
                }
                inline
              />
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="hf-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'global'}
            className={activeTab === 'global' ? 'on' : ''}
            onClick={() => setActiveTab('global')}
          >
            <Pin size={13} /> Global pins <span className="ct">{globalPins.length}</span>
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'tag'}
            className={activeTab === 'tag' ? 'on' : ''}
            onClick={() => setActiveTab('tag')}
          >
            <Hash size={13} /> Per tag
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'homepage'}
            className={activeTab === 'homepage' ? 'on' : ''}
            onClick={() => setActiveTab('homepage')}
          >
            <Home size={13} /> Homepage <span className="ct">{homepageModels.length}</span>
          </button>
        </div>

        {/* Global Pins Tab */}
        {activeTab === 'global' && (
          <div className="hf-col hf-gap-16">
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Add or update global pin</h3>
                <span className="hf-help" style={{ marginTop: 0 }}>
                  An existing pin for the same username will be overwritten.
                </span>
              </div>
              <div className="hf-card-body">
                <form onSubmit={handleGlobalPinSubmit} className="hf-row hf-gap-10 hf-wrap" style={{ alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <div className="hf-label-row">
                      <span className="hf-label">Model username</span>
                    </div>
                    <div className="hf-input">
                      <span className="hf-icon-l"><AtSign size={14} /></span>
                      <input
                        value={globalPinForm.username}
                        onChange={(e) => setGlobalPinForm({ ...globalPinForm, username: e.target.value })}
                        placeholder="username"
                      />
                    </div>
                  </div>
                  <div style={{ flex: '0 0 130px' }}>
                    <div className="hf-label-row">
                      <span className="hf-label">Position</span>
                    </div>
                    <div className="hf-input">
                      <input
                        type="number"
                        min={1}
                        value={globalPinForm.position}
                        onChange={(e) =>
                          setGlobalPinForm({ ...globalPinForm, position: Number(e.target.value) })
                        }
                      />
                    </div>
                  </div>
                  <button type="submit" className="hf-btn primary" disabled={upsertGlobalMutation.isPending}>
                    {upsertGlobalMutation.isPending
                      ? <><Loader2 size={14} className="spin" /> Saving…</>
                      : <><Plus size={14} /> Save pin</>}
                  </button>
                </form>
              </div>
            </div>

            <div className="hf-card flush">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Current global pins</h3>
                <span className="hf-help" style={{ marginTop: 0 }}>{globalPins.length} total</span>
              </div>
              {globalPins.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <EmptyState
                    Icon={Pin}
                    title="No global pins yet"
                    description="Add a pin above to surface a model on every page."
                  />
                </div>
              ) : (
                <table className="hf-table">
                  <thead>
                    <tr>
                      <th className="num" style={{ width: 70 }}>#</th>
                      <th>Username</th>
                      <th>Model Name</th>
                      <th style={{ width: 64 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...globalPins]
                      .sort((a, b) => a.position - b.position)
                      .map((pin) => (
                        <tr key={pin.id}>
                          <td className="num"><span className="hf-mono">{pin.position}</span></td>
                          <td><span className="hf-mono">@{pin.username}</span></td>
                          <td style={{ color: 'var(--fg-2)' }}>{pin.modelName || '—'}</td>
                          <td>
                            <button
                              type="button"
                              className="hf-btn ghost sm"
                              onClick={() => handleGlobalDelete(pin.username)}
                              disabled={deleteGlobalMutation.isPending}
                              aria-label={`Delete pin for ${pin.username}`}
                              style={{ color: 'var(--danger)' }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Tag Pins Tab */}
        {activeTab === 'tag' && (
          <div className="hf-col hf-gap-16">
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Add or update tag pin</h3>
              </div>
              <div className="hf-card-body">
                <form onSubmit={handleTagPinSubmit} className="hf-row hf-gap-10 hf-wrap" style={{ alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 180px' }}>
                    <div className="hf-label-row"><span className="hf-label">Tag slug</span></div>
                    <div className="hf-input">
                      <span className="hf-icon-l"><Hash size={14} /></span>
                      <input
                        value={tagPinForm.tagSlug}
                        onChange={(e) => setTagPinForm({ ...tagPinForm, tagSlug: e.target.value })}
                        placeholder="e.g. blonde"
                      />
                    </div>
                  </div>
                  <div style={{ flex: '1 1 180px' }}>
                    <div className="hf-label-row"><span className="hf-label">Model username</span></div>
                    <div className="hf-input">
                      <span className="hf-icon-l"><AtSign size={14} /></span>
                      <input
                        value={tagPinForm.username}
                        onChange={(e) => setTagPinForm({ ...tagPinForm, username: e.target.value })}
                        placeholder="username"
                      />
                    </div>
                  </div>
                  <div style={{ flex: '0 0 110px' }}>
                    <div className="hf-label-row"><span className="hf-label">Position</span></div>
                    <div className="hf-input">
                      <input
                        type="number"
                        min={1}
                        value={tagPinForm.position}
                        onChange={(e) =>
                          setTagPinForm({ ...tagPinForm, position: Number(e.target.value) })
                        }
                      />
                    </div>
                  </div>
                  <button type="submit" className="hf-btn primary" disabled={upsertTagMutation.isPending}>
                    {upsertTagMutation.isPending
                      ? <><Loader2 size={14} className="spin" /> Saving…</>
                      : <><Plus size={14} /> Save tag pin</>}
                  </button>
                </form>
              </div>
            </div>

            <div className="hf-card flush">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Pins for a tag</h3>
              </div>
              <div className="hf-card-body">
                <div className="hf-row hf-gap-10" style={{ alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <div className="hf-label-row"><span className="hf-label">Tag slug</span></div>
                    <div className="hf-input">
                      <span className="hf-icon-l"><Hash size={14} /></span>
                      <input
                        value={selectedTagForList}
                        onChange={(e) => setSelectedTagForList(e.target.value)}
                        placeholder="enter slug to load pins"
                      />
                    </div>
                  </div>
                </div>

                {selectedTagForList && (
                  <div style={{ marginTop: 14 }}>
                    {tagPins.length === 0 ? (
                      <EmptyState
                        compact
                        Icon={Hash}
                        title={`No pins for "${selectedTagForList}"`}
                        description="Use the form above to add the first pin for this tag."
                      />
                    ) : (
                      <table className="hf-table">
                        <thead>
                          <tr>
                            <th className="num" style={{ width: 70 }}>#</th>
                            <th>Username</th>
                            <th>Model Name</th>
                            <th style={{ width: 64 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...tagPins].sort((a, b) => a.position - b.position).map((pin) => (
                            <tr key={pin.id}>
                              <td className="num"><span className="hf-mono">{pin.position}</span></td>
                              <td><span className="hf-mono">@{pin.username}</span></td>
                              <td style={{ color: 'var(--fg-2)' }}>{pin.modelName || '—'}</td>
                              <td>
                                <button
                                  type="button"
                                  className="hf-btn ghost sm"
                                  onClick={() => handleTagDelete(pin.username)}
                                  disabled={deleteTagMutation.isPending}
                                  aria-label={`Delete pin for ${pin.username}`}
                                  style={{ color: 'var(--danger)' }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Homepage Tab */}
        {activeTab === 'homepage' && (
          <div className="hf-col hf-gap-16">
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Add to homepage</h3>
                <span className="hf-help" style={{ marginTop: 0 }}>
                  Position is assigned automatically at the end.
                </span>
              </div>
              <div className="hf-card-body">
                <form onSubmit={handleHomepageAdd} className="hf-row hf-gap-10" style={{ alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <div className="hf-label-row"><span className="hf-label">Model username</span></div>
                    <div className="hf-input">
                      <span className="hf-icon-l"><AtSign size={14} /></span>
                      <input
                        value={homepageUsername}
                        onChange={(e) => setHomepageUsername(e.target.value)}
                        placeholder="username"
                      />
                    </div>
                  </div>
                  <button type="submit" className="hf-btn primary" disabled={addHomepageMutation.isPending}>
                    {addHomepageMutation.isPending
                      ? <><Loader2 size={14} className="spin" /> Adding…</>
                      : <><Plus size={14} /> Add to homepage</>}
                  </button>
                </form>
              </div>
            </div>

            <div className="hf-card flush">
              <div className="hf-card-header">
                <div>
                  <h3 className="hf-card-title">Homepage models</h3>
                  <span className="hf-help" style={{ marginTop: 2 }}>
                    Drag rows to reorder — changes save automatically.
                  </span>
                </div>
                {reorderHomepageMutation.isPending && (
                  <span className="hf-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--fg-3)', marginTop: 0 }}>
                    <Loader2 size={12} className="spin" /> Saving order…
                  </span>
                )}
              </div>
              {sortedHomepageModels.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <EmptyState
                    Icon={Home}
                    title="No homepage models yet"
                    description="Use the form above to pick the first model for the homepage."
                  />
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedHomepageModels.map(m => m.username)}
                    strategy={verticalListSortingStrategy}
                  >
                    <table className="hf-table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th className="num" style={{ width: 60 }}>#</th>
                          <th>Username</th>
                          <th>Model Name</th>
                          <th style={{ width: 64 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedHomepageModels.map((model) => (
                          <SortableHomepageRow
                            key={model.username}
                            model={model}
                            onDelete={handleHomepageDelete}
                            isDeleting={deleteHomepageMutation.isPending}
                          />
                        ))}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ModelPinsPage;
